import React, { useState, useEffect } from 'react';
import Dialog from '@material-ui/core/Dialog';
import DialogContent from '@material-ui/core/DialogContent';
import DialogContentText from '@material-ui/core/DialogContentText';
import { useDispatch, useSelector } from 'react-redux';
import { updateCommentDialog } from 'store/dialog/reducer';
import CustomButton from 'components/CustomInput/Button';
import styled from 'styled-components/macro';
import CustomTextField from 'components/Input/CustomTextField';
import UploadWrapper from 'components/UploadWrapper';
import UploadDisplay from 'components/uploadFile/UploadDisplay';
import { produce } from 'utility/general';
import CustomDatePicker from 'components/Input/CustomDatePicker';
import TextHeader from 'components/CustomText/TextHeader';
import CustomOutlineSelect from 'components/Input/CustomOutlineSelect';

const SContent = styled.div`
    min-width: 400px;
    font-weight: 500;
`;

const SActionLayout = styled.div`
    display: flex;
    justify-content: flex-end;
    margin-top: 20px;
    padding-bottom: 10px;
`;

const CommentDialog = () => {
    const isShow = useSelector((state) => state.dialog.commentDialog.isShow);
    const title = useSelector((state) => state.dialog.commentDialog.title);
    const description = useSelector((state) => state.dialog.commentDialog.description);
    const placeholder = useSelector((state) => state.dialog.commentDialog.placeholder);
    const confirmText = useSelector((state) => state.dialog.confirmDialog.confirmButtonText);
    const cancelText = useSelector((state) => state.dialog.confirmDialog.cancelButtonText);
    const showExpiryDate = useSelector((state) => state.dialog.commentDialog.showExpiryDate);
    const showUploadButton = useSelector((state) => state.dialog.commentDialog.showUploadButton);
    const uploadKey = useSelector((state) => state.dialog.commentDialog.uploadKey);
    const isRequired = useSelector((state) => state.dialog.commentDialog.isRequired);
    const showClusterSelect = useSelector((state) => state.dialog.commentDialog.showClusterSelect);
    const dispatch = useDispatch();

    const getDefaultExpireDay = () => {
        const date = new Date();
        date.setDate(date.getDate() + 90);
        return date;
    };

    const [expireDay, setExpireDay] = useState(getDefaultExpireDay());
    const [uploadFileNames, setUploadFileNames] = useState([]);

    const [comment, setComment] = useState('');
    const clusters = useSelector((state) => state.system.clusters);
    const [formDataValid, setFormDataValid] = useState(isRequired ? false : true);
    const [forceShowError, setForceShowError] = useState(false);
    const [isFileUploading, setFileUploading] = useState(false);
    const [clusterSelect, setClusterSelect] = useState(clusters[0].clusterId);

    useEffect(() => {
        if (!isShow) {
            setComment('');
            setForceShowError(false);
            setUploadFileNames([]);
            setExpireDay(getDefaultExpireDay());
            setClusterSelect(clusters[0].clusterId);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isShow]);

    const handleOk = () => {
        setForceShowError(true);
        if (formDataValid) {
            dispatch(
                updateCommentDialog({
                    result: true,
                    comment: comment,
                    isShow: false,
                    fileIds: uploadFileNames.map((item) => item.key),
                    expiryDate: showExpiryDate ? expireDay : null,
                    cluster: clusterSelect,
                }),
            );
        }
    };

    const handleCancel = () => {
        dispatch(
            updateCommentDialog({
                result: false,
                isShow: false,
            }),
        );
    };

    const onFileUploaded = (list) => {
        setUploadFileNames([...uploadFileNames, ...list]);
        setFileUploading(false);
    };

    const onFileRemove = (index) => {
        setUploadFileNames(
            produce((draft) => {
                draft.splice(index, 1);
            }),
        );
    };

    return (
        isShow && (
            <Dialog open={isShow}>
                <DialogContent>
                    <SContent>
                        {title && <TextHeader style={{ marginBottom: 10 }}>{title}</TextHeader>}
                        {description && (
                            <DialogContentText
                                style={{
                                    fontWeight: !title ? 'bold' : 'normal',
                                    fontSize: !title ? '16px' : '14px',
                                }}
                            >
                                {description}
                            </DialogContentText>
                        )}
                        <CustomTextField
                            isTextArea
                            placeholder={placeholder ?? 'Reasons'}
                            onChange={(e) => {
                                setComment(e.target.value);
                            }}
                            value={comment}
                            validators={isRequired ? ['isRequired'] : []}
                            isValidValue={(valid) => setFormDataValid(valid)}
                            forceErrorDisplay={forceShowError}
                            maxLength={255}
                        />

                        {showClusterSelect && (
                            <div style={{ marginTop: 15 }}>
                                <CustomOutlineSelect
                                    choices={clusters
                                        .filter((item) => item.clusterCode !== 'OTHERS')
                                        .map((item) => {
                                            return { key: item.clusterId, display: item.clusterCode };
                                        })}
                                    value={clusterSelect}
                                    onChange={(e) => {
                                        setClusterSelect(e.target.value);
                                    }}
                                    label={"Cluster to distribute"}
                                />
                            </div>
                        )}

                        {showExpiryDate && (
                            <div style={{ marginTop: 30 }}>
                                <CustomDatePicker
                                    value={expireDay}
                                    label={'Expire Date'}
                                    handleChangeDate={(e) => {
                                        setExpireDay(e.target.value);
                                    }}
                                    minDate={new Date().getTime()}
                                    // forceErrorDisplay={forceShowError}
                                    // validators={['isRequired']}
                                    // isValidValue={(isValid) => {
                                    //     setValidation(
                                    //         produce((draft) => {
                                    //             draft.expiryDate = isValid;
                                    //         }),
                                    //     );
                                    // }}
                                />
                            </div>
                        )}
                    </SContent>

                    {uploadFileNames.length > 0 && (
                        <div style={{ marginTop: 10 }}>
                            <UploadDisplay
                                fileList={uploadFileNames.map((item) => ({ name: item.fileName }))}
                                enableRemove
                                onRemove={onFileRemove}
                            />
                        </div>
                    )}

                    <div style={{ marginTop: 15 }}>
                        {showUploadButton && (
                            <UploadWrapper
                                optional
                                inputStyle
                                key={uploadKey}
                                onFileUploadFinish={onFileUploaded}
                                onUploadFailed={() => setFileUploading(false)}
                                onLoadingChange={(isLoading) => {
                                    setFileUploading(isLoading);
                                }}
                                multiple
                            />
                        )}
                    </div>

                    <SActionLayout>
                        <div>
                            <CustomButton type={'cancel'} onClick={handleCancel}>
                                {cancelText ?? 'Cancel'}
                            </CustomButton>
                            <CustomButton
                                loading={isFileUploading}
                                style={{ marginLeft: 20 }}
                                onClick={handleOk}
                                autoFocus
                            >
                                {confirmText ?? 'Confirm'}
                            </CustomButton>
                        </div>
                    </SActionLayout>
                </DialogContent>
            </Dialog>
        )
    );
};

export default CommentDialog;
